import React, { useState, useEffect, useRef } from 'react';
import { IndianRupee, ArrowLeft, Calendar, Wallet, X, Eye, EyeOff } from 'lucide-react';
import { supabase, SUPABASE_ENABLED } from './lib/supabaseClient';

interface CollectionRecord {
  date: string;
  time: string;
  mode: string;
  amount: string;
}

interface Customer {
  id: number;
  name: string;
  number: string | null;
  history: CollectionRecord[];
  emi_amount?: number; // Supabase: active_accounts.emi_amount (optional for demo data)
}

// Supabase table types (minimal fields used by the UI)
// Removed unused LedgerAccount interface in favor of active_accounts view

interface LedgerCollection {
  id: number;
  account_id: number;
  date: string; // ISO date string (YYYY-MM-DD)
  amount: number;
  mode: 'Cash' | 'UPI' | 'PP';
  created_at: string;
  remarks?: string | null;
}

// Supabase view: ledger_summary (per-account summary)
interface LedgerSummaryItem {
  id: number;
  account_name: string;
  phone: string | null;
  account_no: string | null;
  principal_amount: number;
  emi_amount: number;
  total_collected: number;
  repayment_done: number;
  outstanding_due: number;
}

const collectionsData: Customer[] = [
  { id: 1, name: "FEBIN 1", number: "8075247562", history: [] },
  { id: 2, name: "FEBIN 2", number: null, history: [] },
  { id: 3, name: "FEBIN 3", number: null, history: [] },
  { id: 4, name: "FEBIN 4", number: null, history: [] },
  { id: 5, name: "SAHID", number: null, history: [] },
  { id: 6, name: "JAVED", number: "7902455547", history: [] },
  { id: 7, name: "ASIF", number: "6325699390", history: [] },
  { id: 8, name: "MANOHAR", number: "9959493023", history: [] },
  { id: 9, name: "JAI SANKAR", number: null, history: [] },
  { id: 10, name: "MANOHAR - SIVA", number: null, history: [] },
  { id: 11, name: "MANOHAR RAJENDRA", number: null, history: [] },
  { id: 12, name: "VENKATESH - WATER", number: null, history: [] },
  { id: 13, name: "YS MANI", number: "8977961210", history: [] },
  { id: 14, name: "POLIAN", number: "9398990801", history: [] },
  { id: 15, name: "BRAMHIAH AC", number: "9502008211", history: [] },
  { id: 16, name: "SURYA NARAYANA", number: "8686688777", history: [] },
  { id: 17, name: "KIRAN", number: "9908842557", history: [] },
  { id: 18, name: "HUSSAIN", number: "8897441180", history: [] },
  { id: 19, name: "ABDULLA", number: "8073805648", history: [] },
  { id: 20, name: "RAVI MUNI", number: null, history: [] },
  { id: 21, name: "RAVI - JAVED", number: null, history: [] },
  { id: 22, name: "RAVI", number: null, history: [] },
  { id: 23, name: "RAVI - YS MANI", number: null, history: [] },
  { id: 24, name: "FEBIN - YS MANI", number: null, history: [] },
  { id: 25, name: "MURALI", number: null, history: [] },
  { id: 26, name: "IZAZ", number: null, history: [] },
  { id: 27, name: "RIYAZ", number: null, history: [] },
  { id: 28, name: "SONU", number: null, history: [] },
  { id: 29, name: "Hussain - Manikanta", number: null, history: [] },
  { id: 30, name: "Manohar Guru", number: null, history: [] }
];

function App() {
  const [collections, setCollections] = useState<Customer[]>(collectionsData);
  // Removed legacy input/mode states in EMI-based workflow
  // PP Modal state (Option 1: unlimited PP with remarks)
  const [showPpModal, setShowPpModal] = useState<boolean>(false);
  const [ppModalAccountId, setPpModalAccountId] = useState<number | null>(null);
  const [ppAmount, setPpAmount] = useState<string>('');
  const [ppRemarks, setPpRemarks] = useState<string>('');
  const [ppError, setPpError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<'under_construction' | 'list' | 'history' | 'ledger'>('under_construction');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedPersonName, setSelectedPersonName] = useState<string | null>(null);
  const [personLedger, setPersonLedger] = useState<LedgerSummaryItem[]>([]);
  const [personLedgerRows, setPersonLedgerRows] = useState<{
    account_id: number;
    account_name: string;
    date: string;
    amount: number;
    mode: string;
    created_at: string;
  }[]>([]);
  const [centralAccounts, setCentralAccounts] = useState<LedgerSummaryItem[]>([]);
  const [centralAccountMeta, setCentralAccountMeta] = useState<Record<number, {
    status?: string;
    loan_date?: string;
    term_days?: number;
    amount_held?: number;
  }>>({});
  const [showModal, setShowModal] = useState(false);
  const [modalCustomer, setModalCustomer] = useState<Customer | null>(null);
  const [todayTotal, setTodayTotal] = useState(0);
  const [usingSupabase, setUsingSupabase] = useState<boolean>(SUPABASE_ENABLED);
  const [loadingCollections, setLoadingCollections] = useState<boolean>(false);
  const [showTotal, setShowTotal] = useState<boolean>(false);
  const [showPinModal, setShowPinModal] = useState<boolean>(false);
  const [pinValue, setPinValue] = useState<string>('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [todaysCollections, setTodaysCollections] = useState<LedgerCollection[]>([]);
  
  // TEMP: Under Construction (rendered via currentPage state below)

  // Per-account ledger navigation/state
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  type LedgerAccount = {
    id: number;
    account_name: string;
    emi_amount?: number | null;
    principal_amount?: number | null;
    amount_held?: number | null;
    outstanding_due?: number | null;
    status?: string | null;
    loan_date?: string | null;
  } | null;
  const [ledgerAccount, setLedgerAccount] = useState<LedgerAccount>(null);
  const [ledgerRows, setLedgerRows] = useState<LedgerCollection[]>([]);
  const [ledgerSummary, setLedgerSummary] = useState<{ totalCollected: number; pendingDays: number; lastPaymentDate: string | null }>({ totalCollected: 0, pendingDays: 0, lastPaymentDate: null });

  const handleUnlockTotal = () => {
    if (pinValue === '9700') {
      setShowTotal(true);
      setShowPinModal(false);
      setPinError(null);
      setPinValue('');
    } else {
      setPinError('Incorrect PIN');
    }
  };

  const handleMaskTotal = () => {
    setShowTotal(false);
  };

  const TODAY_TZ = 'Asia/Kolkata';
  const today = new Intl.DateTimeFormat('en-US', {
    timeZone: TODAY_TZ,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(new Date());


  // IST display date used in local UI/state comparisons
  const todayISTDisplay = new Intl.DateTimeFormat('en-IN', {
    timeZone: TODAY_TZ
  }).format(new Date());

  const formatISTTime = (dateLike: Date | string) =>
    new Intl.DateTimeFormat('en-IN', {
      timeZone: TODAY_TZ,
      hour: '2-digit',
      minute: '2-digit'
    }).format(typeof dateLike === 'string' ? new Date(dateLike) : dateLike);

  // IST date display for arbitrary YYYY-MM-DD values
  const formatISTDateDisplay = (dateIso: string) =>
    new Intl.DateTimeFormat('en-IN', {
      timeZone: TODAY_TZ,
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(new Date(dateIso));

  // Helper: Force IST (UTC+5:30) and apply 12:45 AM cutoff (YYYY-MM-DD)
  function getISTCollectionDate(): string {
    const nowUTC = new Date();
    // Convert to IST explicitly (UTC + 5h 30m)
    const ist = new Date(nowUTC.getTime() + (5.5 * 60 * 60 * 1000));

    // Apply 12:45 AM cutoff
    const cutoff = new Date(ist);
    cutoff.setHours(0, 45, 0, 0);

    // If time is before 12:45 AM IST → assign previous date
    if (ist < cutoff) {
      ist.setDate(ist.getDate() - 1);
    }

    // Return YYYY-MM-DD
    return ist.toISOString().slice(0, 10);
  }

  function getISTCollectionDateDisplay(): string {
    const now = new Date();
    const istStr = now.toLocaleString('en-US', { timeZone: TODAY_TZ });
    const ist = new Date(istStr);
    const hours = ist.getHours();
    const minutes = ist.getMinutes();
    if (hours === 0 && minutes < 45) {
      ist.setDate(ist.getDate() - 1);
    }
    return new Intl.DateTimeFormat('en-IN', { timeZone: TODAY_TZ }).format(ist);
  }

  // Helper: has any full payment (Cash/UPI) today for account
  const hasFullPaymentToday = (accountId: number) =>
    todaysCollections.some(row => row.account_id === accountId && (row.mode === 'Cash' || row.mode === 'UPI'));

  // Load accounts and today's collections from Supabase if configured
  useEffect(() => {
    const loadFromSupabase = async () => {
      setLoadingCollections(true);
      if (!SUPABASE_ENABLED) {
        setUsingSupabase(false);
        setLoadingCollections(false);
        return;
      }

      // Fetch active accounts from view with EMI amount
      const { data: accounts, error: accErr } = await supabase
        .from('active_accounts')
        .select('id, account_name, emi_amount')
        .order('id');

      if (accErr || !accounts) {
        console.error('Failed to load accounts:', accErr);
        setUsingSupabase(false);
        setLoadingCollections(false);
        return;
      }

      // Fetch today's collections (ALL rows) via helper
      await loadTodaysCollectionsFromSupabase();

      const mapped: Customer[] = (accounts as any[]).map((a: any) => {
        const history: CollectionRecord[] = [];
        return {
          id: a.id,
          name: a.account_name,
          number: null,
          history,
          emi_amount: Number(a.emi_amount) || 0
        };
      });

      setCollections(mapped);
      setUsingSupabase(true);
      setLoadingCollections(false);

      // After loading collections, refresh today's total from Supabase using IST date
      await loadTodayTotalSupabase();
    };

    loadFromSupabase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch all rows for today's IST date from Supabase and store as array
  const loadTodaysCollectionsFromSupabase = async () => {
    if (!SUPABASE_ENABLED) return;
    const date = getISTCollectionDate();
    const { data, error } = await supabase
      .from('ledger_collections')
      .select('id, account_id, date, amount, mode, created_at')
      .eq('date', date);
    if (error) {
      console.error('Failed to load today collections:', error);
      setTodaysCollections([]);
      return;
    }
    setTodaysCollections((data || []) as unknown as LedgerCollection[]);
  };

  // Supabase: load today's total by IST date (12:45 AM cutoff)
  const loadTodayTotalSupabase = async () => {
    if (!SUPABASE_ENABLED) return;
    const date = getISTCollectionDate();
    const { data, error } = await supabase
      .from('ledger_collections')
      .select('amount')
      .eq('date', date);
    if (error) {
      console.error('Failed to load today total:', error);
      return;
    }
    let sum = 0;
    (data || []).forEach((row: any) => { sum += Number(row.amount) || 0; });
    setTodayTotal(sum);
  };

  // Removed local recompute of today total; rely on Supabase-only loader

  // Removed legacy amount/mode handlers in favor of EMI-based buttons

  // (removed) handleViewHistory — no longer used in the single-row list UI

  // (removed) normalizePersonName — no longer required for central ledger navigation

  // Load all accounts for a person from ledger_summary view
  // (removed) handleViewLedger — replaced by handleViewCentralLedgerAll for central view

  // Central Ledger: load all rows across all people
  const handleViewCentralLedgerAll = async () => {
    setSelectedPersonName('All');
    setSelectedAccountId(null);
    if (usingSupabase) {
      // Load all accounts (summary view)
      const { data: accounts, error: accErr } = await supabase
        .from('ledger_summary')
        .select('*')
        .order('id');

      if (!accErr && accounts) {
        setCentralAccounts(accounts as LedgerSummaryItem[]);
        const ids = (accounts as LedgerSummaryItem[]).map(a => a.id);
        // Best-effort: fetch extra meta from base table if available
        if (ids.length > 0) {
          const { data: meta, error: metaErr } = await supabase
            .from('ledger_accounts')
            .select('id, status, loan_date, term_days, amount_held')
            .in('id', ids);
          if (!metaErr && meta) {
            const map: Record<number, any> = {};
            (meta as any[]).forEach(m => { map[m.id] = m; });
            setCentralAccountMeta(map);
          } else {
            setCentralAccountMeta({});
          }
        } else {
          setCentralAccountMeta({});
        }
      } else {
        setCentralAccounts([]);
        setCentralAccountMeta({});
      }

      const { data: rows, error: rowsErr } = await supabase
        .from('ledger_collections')
        .select('account_id, date, amount, mode, created_at, ledger_accounts(account_name)')
        .order('created_at', { ascending: false })
        .limit(1000);

      if (!rowsErr && rows) {
        const mapped = rows.map((r: any) => ({
          account_id: r.account_id,
          account_name: r.ledger_accounts?.account_name || String(r.account_id),
          date: r.date,
          amount: r.amount,
          mode: r.mode,
          created_at: r.created_at,
        }));
        setPersonLedgerRows(mapped);
      } else {
        console.error('Failed to load central ledger rows:', rowsErr);
        setPersonLedgerRows([]);
      }
    } else {
      setPersonLedgerRows([]);
    }
    setPersonLedger([]);
    setCurrentPage('ledger');
  };

  const handleBack = () => {
    setCurrentPage('list');
    setSelectedCustomer(null);
    setSelectedAccountId(null);
  };

  // Removed unused handleUpdateClick to satisfy TypeScript build

  const handleModalClose = () => {
    setShowModal(false);
    setModalCustomer(null);
  };

  const handleModalSubmit = (amount: string, mode: string) => {
    if (!amount || !mode || !modalCustomer) return;

    const today = todayISTDisplay;
    const timestamp = formatISTTime(new Date());

    setCollections(prev => prev.map(c =>
      c.id === modalCustomer.id
        ? {
            ...c,
            history: [...c.history, { date: today, time: timestamp, mode: mode, amount: amount }]
          }
        : c
    ));

    handleModalClose();
  };

  // Removed getTodayCollection; local lock uses Cash/UPI-only check

  const getTotalCollection = (customer: Customer) => {
    return customer.history.reduce((sum, h) => sum + parseFloat(h.amount || '0'), 0);
  };

  // Removed currency formatter from UI to fully hide EMI amounts

  // Option 1: Common insert logic
  const recordCollection = async (
    accountId: number,
    mode: 'Cash' | 'UPI' | 'PP',
    amount: number,
    remarks?: string
  ) => {
    const todayStr = getISTCollectionDateDisplay();
    const collectionDateIso = getISTCollectionDate();
    const timestamp = formatISTTime(new Date());

    // Enforce single full EMI (Cash/UPI) per day
    if (usingSupabase && (mode === 'Cash' || mode === 'UPI')) {
      const { data: existingRows, error: preErr } = await supabase
        .from('ledger_collections')
        .select('id, amount, mode, created_at')
        .eq('account_id', accountId)
        .eq('date', collectionDateIso)
        .in('mode', ['Cash', 'UPI'])
        .limit(1);
      if (!preErr && existingRows && existingRows.length > 0) {
        // Already collected today via Cash/UPI — reflect in local history for context and bail
        const row = existingRows[0] as { amount?: number; mode?: string; created_at?: string };
        const timeStr = row?.created_at
          ? new Date(row.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
          : timestamp;
        setCollections(prev => prev.map(c =>
          c.id === accountId
            ? {
                ...c,
                history: c.history.some(h => h.date === todayStr)
                  ? c.history
                  : [...c.history, { date: todayStr, time: timeStr, mode: String(row?.mode || 'Collected'), amount: String(row?.amount ?? amount ?? 0) }]
              }
            : c
        ));
        return;
      }
    }

    // Insert row in Supabase
    if (SUPABASE_ENABLED) {
      const insertPayload: any = {
        account_id: accountId,
        amount: Number(amount),
        mode,
        date: collectionDateIso,
        collected_by: 'collector'
      };
      if (remarks) insertPayload.remarks = remarks;
      const { error } = await supabase.from('ledger_collections').insert(insertPayload);
      if (error) {
        console.error('Insert failed:', error);
      } else {
        // Append to todaysCollections for immediate UI lock and visibility
        setTodaysCollections(prev => [
          ...prev,
          {
            id: -1,
            account_id: accountId,
            date: collectionDateIso,
            amount: Number(amount),
            mode,
            created_at: new Date().toISOString()
          } as LedgerCollection
        ]);
        await loadTodayTotalSupabase();
      }
    }

    // Always reflect in local customer history for context
    setCollections(prev => prev.map(c =>
      c.id === accountId
        ? {
            ...c,
            history: [...c.history, { date: todayStr, time: timestamp, mode, amount: String(amount) }]
          }
        : c
    ));
  };

  const handleCashCollect = async (customerId: number, emiAmount: number = 0) => {
    if (hasFullPaymentToday(customerId)) return; // ignore clicks when already collected
    await recordCollection(customerId, 'Cash', emiAmount || 0);
  };

  const handleUpiCollect = async (customerId: number, emiAmount: number = 0) => {
    if (hasFullPaymentToday(customerId)) return; // ignore clicks when already collected
    await recordCollection(customerId, 'UPI', emiAmount || 0);
  };

  const openPpModal = (customerId: number) => {
    setPpModalAccountId(customerId);
    setPpAmount('');
    setPpRemarks('');
    setPpError(null);
    setShowPpModal(true);
  };

  const submitPpModal = async () => {
    const id = ppModalAccountId;
    if (!id) return;
    const val = Number(ppAmount || '0');
    if (!val || isNaN(val) || val <= 0) {
      setPpError('Enter a valid amount');
      return;
    }
    await recordCollection(id, 'PP', val, ppRemarks || undefined);
    setShowPpModal(false);
    setPpModalAccountId(null);
    setPpAmount('');
    setPpRemarks('');
    setPpError(null);
  };

  // Load ledger for a single account
  const loadAccountLedger = async (accountId: number) => {
    if (!SUPABASE_ENABLED) return;
    try {
      const { data: account, error: accErr } = await supabase
        .from('ledger_accounts')
        .select('*')
        .eq('id', accountId)
        .single();
      if (accErr) {
        console.error('Failed to load account:', accErr);
        setLedgerAccount(null);
      } else {
        setLedgerAccount(account as any);
      }

      const { data: rows, error: rowsErr } = await supabase
        .from('ledger_collections')
        .select('id, account_id, date, amount, mode, created_at, remarks')
        .eq('account_id', accountId)
        .order('date', { ascending: true })
        .order('created_at', { ascending: true });
      if (rowsErr) {
        console.error('Failed to load ledger rows:', rowsErr);
        setLedgerRows([]);
        setLedgerSummary({ totalCollected: 0, pendingDays: 0, lastPaymentDate: null });
      } else {
        const list = (rows || []) as unknown as LedgerCollection[];
        setLedgerRows(list);
        const totalCollected = list.reduce((s, r) => s + (Number(r.amount) || 0), 0);
        const lastPaymentDate = list.length > 0 ? list[list.length - 1].date : null;

        // Compute No. of Days Pending (monthly EMI)
        const todayIso = getISTCollectionDate();
        const [ty, tm] = todayIso.split('-').slice(0, 2).map(n => parseInt(n, 10));
        const dueDay = (() => {
          const ld = (account as any)?.loan_date as string | undefined;
          if (!ld) return 1;
          const d = new Date(ld);
          return d.getDate() || 1;
        })();
        const daysInMonth = (y: number, mIdx: number) => new Date(y, mIdx + 1, 0).getDate();
        const clampDay = (y: number, mIdx: number, day: number) => Math.min(day, daysInMonth(y, mIdx));
        const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

        const currDueDay = clampDay(ty, tm - 1, dueDay);
        const dueDateIso = `${ty}-${pad(tm)}-${pad(currDueDay)}`;
        const prevMonth = tm === 1 ? 12 : tm - 1;
        const prevYear = tm === 1 ? ty - 1 : ty;
        const prevDueDay = clampDay(prevYear, prevMonth - 1, dueDay);
        const prevDueDateIso = `${prevYear}-${pad(prevMonth)}-${pad(prevDueDay)}`;

        const hasFullEmiThisCycle = list.some(r => (r.mode === 'Cash' || r.mode === 'UPI') && r.date >= prevDueDateIso && r.date <= dueDateIso);
        let pendingDays = 0;
        if (!hasFullEmiThisCycle) {
          // If today is before due date, pending is 0; else days since due date
          if (todayIso > dueDateIso) {
            const diffMs = new Date(todayIso).getTime() - new Date(dueDateIso).getTime();
            pendingDays = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
          } else {
            pendingDays = 0;
          }
        }

        setLedgerSummary({ totalCollected, pendingDays, lastPaymentDate });
      }
    } catch (e) {
      console.error('loadAccountLedger error:', e);
    }
  };

  const openAccountLedger = async (accountId: number) => {
    setSelectedPersonName(null);
    setSelectedAccountId(accountId);
    setCurrentPage('ledger');
  };

  useEffect(() => {
    if (currentPage === 'ledger' && selectedAccountId) {
      loadAccountLedger(selectedAccountId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedAccountId]);

  // (removed) getLastFiveCollections — recent history panel removed in new design

  // Under Construction Page
  if (currentPage === 'under_construction') {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
        <div className="max-w-xl text-center">
          <h1 className="text-4xl font-bold mb-4">Under Construction</h1>
          <p className="text-xl mb-6">This page is currently under construction.</p>
          <p className="text-lg text-white/80">Pls contact your developer for update</p>
        </div>
      </div>
    );
  }

  // List Page
  if (currentPage === 'list') {
    return (
      <div className="min-h-screen bg-black text-[#E6E6E6] font-sans w-full overflow-x-hidden">
        {/* Header Row: Date and Today's Total in one line */}
        <div className="bg-black border-b border-[#222222] px-4 py-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[26px] font-bold text-[#E6E6E6] px-2 md:px-4">
              {today}
            </div>
            <div className="flex items-center gap-3 text-[#E6E6E6] px-2 md:px-4">
              <span className="text-lg md:text-xl font-semibold">
                Today’s Total: {showTotal ? `₹${todayTotal.toFixed(2)}` : '••••'}
              </span>
              {!showTotal ? (
                <button
                  aria-label="Show total"
                  className="text-[#E6E6E6]"
                  onClick={() => { setPinError(null); setPinValue(''); setShowPinModal(true); }}
                >
                  <Eye size={24} />
                </button>
              ) : (
                <button
                  aria-label="Hide total"
                  className="text-[#E6E6E6]"
                  onClick={handleMaskTotal}
                >
                  <EyeOff size={24} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Collection List - Tablet-friendly single row layout (monochrome) */}
        <div className="p-4 md:p-6 w-full overflow-x-hidden">
          {loadingCollections ? (
            <div className="text-white p-6 text-3xl">Loading...</div>
          ) : collections.map((customer) => {
            const hasCashOrUpiTodayLocal = customer.history.some(
              h => h.date === getISTCollectionDateDisplay() && (h.mode === 'Cash' || h.mode === 'UPI')
            );
            const hasFullPaymentToday = (accountId: number) =>
              todaysCollections.some(row => row.account_id === accountId && (row.mode === 'Cash' || row.mode === 'UPI'));
            const isLocked = usingSupabase ? hasFullPaymentToday(customer.id) : hasCashOrUpiTodayLocal;

            return (
              <div
                key={customer.id}
                className="flex flex-row items-center justify-between gap-4 py-4 px-4 border-b border-[#222222] bg-black w-full overflow-x-hidden flex-wrap"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {/* Name column (25%) */}
                <div className="shrink-0 basis-[25%] min-w-[25%] text-left">
                  <button
                    onClick={() => openAccountLedger(customer.id)}
                    className="text-[#E6E6E6] font-bold text-[26px] tracking-[0.5px] leading-none truncate"
                    title={customer.name}
                  >
                    {customer.name}
                  </button>
                </div>
                {/* Buttons (75%) */}
                <div className="flex flex-row gap-3 flex-shrink-0 items-center justify-end text-right">
                  <>
                      {/* Cash EMI */}
                      <button
                        onClick={() => handleCashCollect(customer.id, customer.emi_amount || 0)}
                        disabled={isLocked}
                        className={`flex items-center justify-center text-center px-4 py-2 rounded-xl border-2 ${
                          isLocked
                            ? 'bg-[#333333] text-[#AAAAAA] border-[#333333] cursor-not-allowed'
                            : 'bg-[#000000] text-[#E6E6E6] border-[#888888] hover:bg-[#DDDDDD] hover:text-[#111111] transition-all duration-150'
                        } h-[46px] min-w-[90px] text-[18px] font-semibold`}
                        title={isLocked ? 'Collected' : 'Cash'}
                      >
                        {isLocked ? 'Collected' : 'Cash'}
                      </button>

                      {/* UPI EMI */}
                      <button
                        onClick={() => handleUpiCollect(customer.id, customer.emi_amount || 0)}
                        disabled={isLocked}
                        className={`flex items-center justify-center text-center px-4 py-2 rounded-xl border-2 ${
                          isLocked
                            ? 'bg-[#333333] text-[#AAAAAA] border-[#333333] cursor-not-allowed'
                            : 'bg-[#000000] text-[#E6E6E6] border-[#888888] hover:bg-[#DDDDDD] hover:text-[#111111] transition-all duration-150'
                        } h-[46px] min-w-[90px] text-[18px] font-semibold`}
                        title={isLocked ? 'Collected' : 'UPI'}
                      >
                        {isLocked ? 'Collected' : 'UPI'}
                      </button>

                      {/* PP (modal: amount + remarks) */}
                      <button
                        onClick={() => openPpModal(customer.id)}
                        className={`flex items-center justify-center text-center px-4 py-2 rounded-xl border-2 bg-[#000000] text-[#E6E6E6] border-[#888888] hover:bg-[#DDDDDD] hover:text-[#111111] transition-all duration-150 h-[46px] min-w-[60px] text-[18px] font-semibold`}
                        title={'PP'}
                      >
                        {'PP'}
                      </button>
                  </>
                </div>
              </div>
            );
          })}
        </div>

        {/* Update Modal */}
        {showModal && modalCustomer && (
          <UpdateModal
            customer={modalCustomer}
            onClose={handleModalClose}
            onSubmit={handleModalSubmit}
          />
        )}
        {/* PIN Popup */}
        {showPinModal && <PinModal />}

        {/* PP Modal */}
        {showPpModal && <PpModal />}
      </div>
    );
  }

  // History Page
  if (currentPage === 'history' && selectedCustomer) {
    const totalAmount = getTotalCollection(selectedCustomer);
    
    return (
      <div className="min-h-screen bg-black text-white font-sans">
        {/* Header */}
        <div className="bg-black border-b border-[#333] px-4 py-4">
          <button 
            onClick={handleBack}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-2"
          >
            <ArrowLeft size={24} />
            <span className="text-base">Back to List</span>
          </button>
          <h1 className="text-2xl font-bold text-white">{selectedCustomer.name}</h1>
          {selectedCustomer.number && (
            <p className="text-white/70 text-base mt-1">{selectedCustomer.number}</p>
          )}
        </div>

        {/* Summary Card */}
        <div className="p-4">
          <div className="bg-black border border-[#333] rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <Wallet size={24} className="text-white" />
              <span className="text-base font-bold">Total Collection</span>
            </div>
            <p className="text-3xl font-bold">₹{totalAmount.toFixed(2)}</p>
            <p className="text-white/70 text-base mt-2">{selectedCustomer.history.length} transactions</p>
          </div>
        </div>

        {/* Payment History */}
        <div className="px-4 pb-6">
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-3">
            <Calendar size={24} className="text-white/70" />
            Payment History
          </h2>
          
          {selectedCustomer.history.length === 0 ? (
            <div className="bg-black border border-[#333] rounded-xl p-6 text-center">
              <p className="text-white/70 text-base">No payment history yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {selectedCustomer.history.slice().reverse().map((record, index) => (
                <div
                  key={index}
                  className="bg-black border border-[#333] rounded-xl p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-full border border-[#333]">
                        <IndianRupee size={20} className="text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-base text-white">₹{record.amount}</p>
                        <p className="text-white/70 text-base">{record.mode}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-base font-medium text-white">{record.date}</p>
                      <p className="text-white/70 text-base">{record.time}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // PIN Popup Modal
  function PinModal() {
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      if (showPinModal) {
        inputRef.current?.focus();
      }
    }, [showPinModal]);

    useEffect(() => {
      // Preserve focus even when error state changes
      if (showPinModal) {
        inputRef.current?.focus();
      }
    }, [pinError]);

    const handleChangePin: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      const next = (e.target.value || '').replace(/\D/g, '').slice(0, 4);
      setPinValue(next);
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        handleUnlockTotal();
      }
    };

      const handleBlur: React.FocusEventHandler<HTMLInputElement> = () => {
        // Keep the modal open and preserve focus while typing
        if (showPinModal) {
          setTimeout(() => inputRef.current?.focus(), 0);
        }
      };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
        <div className="bg-black border border-[#333333] rounded-xl p-5 w-[320px]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-[#E6E6E6]">Enter PIN</h3>
            <button onClick={() => setShowPinModal(false)} className="text-[#E6E6E6]/70 hover:text-[#E6E6E6]">
              <X size={20} />
            </button>
          </div>
          <input
            ref={inputRef}
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            placeholder="Enter PIN"
            autoFocus
            value={pinValue}
            onChange={handleChangePin}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className="w-full px-4 py-3 rounded-xl bg-[#111111] text-white text-[22px] tracking-widest text-center outline-none border border-[#444444]"
          />
          {pinError && (
            <div className="text-[#FF6B6B] text-sm mb-2">{pinError}</div>
          )}
          <div className="flex items-center justify-end gap-3 mt-2">
            <button
              onClick={() => setShowPinModal(false)}
              className="text-[#E6E6E6] px-3 py-2 border-2 border-[#888888] rounded-[10px] hover:bg-[#DDDDDD] hover:text-[#111111]"
            >
              Cancel
            </button>
            <button
              onClick={handleUnlockTotal}
              className="text-[#111111] bg-[#DDDDDD] px-3 py-2 border-2 border-[#DDDDDD] rounded-[10px] hover:opacity-90"
            >
              Unlock
            </button>
          </div>
        </div>
      </div>
    );
  }

  // PP Modal (amount + remarks)
  function PpModal() {
    const amountRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => { amountRef.current?.focus(); }, []);

    const onEnter: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
      if (e.key === 'Enter') submitPpModal();
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
        <div className="bg-black border border-[#333333] rounded-xl p-5 w-[360px]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-[#E6E6E6]">Part Payment</h3>
            <button onClick={() => setShowPpModal(false)} className="text-[#E6E6E6]/70 hover:text-[#E6E6E6]">
              <X size={20} />
            </button>
          </div>
          <div className="space-y-3">
            <input
              ref={amountRef}
              type="number"
              inputMode="decimal"
              placeholder="Amount"
              value={ppAmount}
              onChange={(e) => setPpAmount(e.target.value)}
              onKeyDown={onEnter}
              className="w-full px-4 py-3 rounded-xl bg-[#111111] text-white text-[18px] outline-none border border-[#444444]"
            />
            <input
              type="text"
              placeholder="Remarks (optional)"
              value={ppRemarks}
              onChange={(e) => setPpRemarks(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-[#111111] text-white text-[16px] outline-none border border-[#444444]"
            />
            {ppError && <div className="text-[#FF6B6B] text-sm">{ppError}</div>}
          </div>
          <div className="flex items-center justify-end gap-3 mt-3">
            <button
              onClick={() => setShowPpModal(false)}
              className="text-[#E6E6E6] px-3 py-2 border-2 border-[#888888] rounded-[10px] hover:bg-[#DDDDDD] hover:text-[#111111]"
            >
              Cancel
            </button>
            <button
              onClick={submitPpModal}
              className="text-[#111111] bg-[#DDDDDD] px-3 py-2 border-2 border-[#DDDDDD] rounded-[10px] hover:opacity-90"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Ledger Page (single account)
  if (currentPage === 'ledger' && selectedAccountId) {
    const acc = ledgerAccount;
    const name = acc?.account_name || `Account ${selectedAccountId}`;
    const status = acc?.status || 'ACTIVE';
    const emi = acc?.emi_amount || 0;
    const principal = acc?.principal_amount || 0;
    const amtHeld = acc?.amount_held || 0;
    const outstanding = acc?.outstanding_due || 0;
    const totalCollected = ledgerSummary.totalCollected || 0;
    // pendingDays used directly in UI below
    const lastPayStr = ledgerSummary.lastPaymentDate ? formatISTDateDisplay(ledgerSummary.lastPaymentDate) : '-';

    return (
      <div className="min-h-screen bg-black text-[#E6E6E6] font-sans w-full overflow-x-hidden">
        {/* Header */}
        <div className="bg-black border-b border-[#222222] px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="text-[#E6E6E6] text-xl px-3 py-2 border-2 border-[#444] rounded-[10px]"
            >
              ← Back
            </button>
            <div className="flex items-center gap-3">
              <div className="text-[26px] font-bold text-[#E6E6E6] truncate px-2">
                {name}
              </div>
              <div className="px-3 py-2 border-2 border-[#888] rounded-full text-sm">
                {status}
              </div>
            </div>
            <button
              onClick={handleViewCentralLedgerAll}
              className="text-white/90 text-sm px-3 py-2 border border-white/40 rounded-[8px] hover:text-white hover:border-white/60"
            >
              Central Ledger
            </button>
          </div>
        </div>

        {/* Summary Grid */}
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-black border border-[#333] rounded-xl p-4 flex justify-between text-xl">
            <span>EMI:</span>
            <span>₹{emi.toLocaleString('en-IN')}</span>
          </div>
          <div className="bg-black border border-[#333] rounded-xl p-4 flex justify-between text-xl">
            <span>Principal:</span>
            <span>₹{principal.toLocaleString('en-IN')}</span>
          </div>
          <div className="bg-black border border-[#333] rounded-xl p-4 flex justify-between text-xl">
            <span>Amt Held:</span>
            <span>₹{amtHeld.toLocaleString('en-IN')}</span>
          </div>
          <div className="bg-black border border-[#333] rounded-xl p-4 flex justify-between text-xl">
            <span>Outstanding:</span>
            <span>₹{outstanding.toLocaleString('en-IN')}</span>
          </div>
          <div className="bg-black border border-[#333] rounded-xl p-4 flex justify-between text-xl">
            <span>Total Collected:</span>
            <span>₹{totalCollected.toLocaleString('en-IN')}</span>
          </div>
          <div className="bg-black border border-[#333] rounded-xl p-4 flex justify-between text-xl">
            <span>No. of Days Pending:</span>
            <span>{(ledgerSummary.pendingDays || 0)}{(ledgerSummary.pendingDays || 0) === 1 ? ' day' : ' days'}</span>
          </div>
          <div className="bg-black border border-[#333] rounded-xl p-4 flex justify-between text-xl md:col-span-2">
            <span>Last Payment On:</span>
            <span>{lastPayStr}</span>
          </div>
        </div>

        {/* Payments List */}
        <div className="px-4 pb-6">
          <h2 className="text-xl font-bold text-[#E6E6E6] mb-3">Payments</h2>
          <div className="bg-black border border-[#333] rounded-xl overflow-hidden">
            {ledgerRows.length === 0 ? (
              <div className="p-6 text-center text-[#E6E6E6]/70">No payments found.</div>
            ) : (
              <div className="divide-y divide-[#222] max-h-[60vh] overflow-y-auto">
                {ledgerRows.map((r) => (
                  <div key={`${r.id}-${r.created_at}`} className="flex items-start justify-between px-4 py-3">
                    {/* Left: Date + Time */}
                    <div className="text-lg">
                      <div className="font-semibold">{formatISTDateDisplay(r.date)}</div>
                      <div className="text-[#AAAAAA] text-base">{formatISTTime(r.created_at)}</div>
                      {r.remarks ? (
                        <div className="text-[#BBBBBB] text-sm mt-1">{r.remarks}</div>
                      ) : null}
                    </div>
                    {/* Middle: Mode */}
                    <div>
                      <span className="px-3 py-1 border-2 border-[#888] rounded-full text-sm">
                        {r.mode}
                      </span>
                    </div>
                    {/* Right: Amount */}
                    <div className="text-xl font-bold">₹{Number(r.amount).toLocaleString('en-IN')}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Ledger Page (all accounts for a person)
  if (currentPage === 'ledger' && selectedPersonName) {
    const totalPrincipal = personLedger.reduce((s, r) => s + (r.principal_amount || 0), 0);
    const totalEmi = personLedger.reduce((s, r) => s + (r.emi_amount || 0), 0);
    const totalCollected = personLedger.reduce((s, r) => s + (r.total_collected || 0), 0);
    const totalOutstanding = personLedger.reduce((s, r) => s + (r.outstanding_due || 0), 0);

    return (
      <div className="min-h-screen bg-black text-white font-sans">
        {/* Header */}
        <div className="bg-black border-b border-[#333] px-4 py-4">
          <button 
            onClick={handleBack}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-2"
          >
            <ArrowLeft size={24} />
            <span className="text-base">Back to List</span>
          </button>
          <h1 className="text-2xl font-bold text-white">{selectedPersonName === 'All' ? 'Central Ledger' : selectedPersonName}</h1>
        </div>

        {/* Totals (hidden on Central Ledger All) */}
        {selectedPersonName !== 'All' && (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-black border border-[#333] rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <Wallet size={20} className="text-white" />
                <span className="text-sm font-bold">Total Principal</span>
              </div>
              <p className="text-2xl font-bold">₹{totalPrincipal.toFixed(2)}</p>
            </div>
            <div className="bg-black border border-[#333] rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <Wallet size={20} className="text-white" />
                <span className="text-sm font-bold">Total EMI</span>
              </div>
              <p className="text-2xl font-bold">₹{totalEmi.toFixed(2)}</p>
            </div>
            <div className="bg-black border border-[#333] rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <Wallet size={20} className="text-white" />
                <span className="text-sm font-bold">Total Collected</span>
              </div>
              <p className="text-2xl font-bold">₹{totalCollected.toFixed(2)}</p>
            </div>
            <div className="bg-black border border-[#333] rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <Wallet size={20} className="text-white" />
                <span className="text-sm font-bold">Outstanding Due</span>
              </div>
              <p className="text-2xl font-bold">₹{totalOutstanding.toFixed(2)}</p>
            </div>
          </div>
        )}

        {/* Accounts List (hidden on Central Ledger All) */}
        {selectedPersonName !== 'All' && (
          <div className="px-4 pb-6">
            <h2 className="text-xl font-bold text-white mb-3">Accounts</h2>
            {personLedger.length === 0 ? (
              <div className="bg-black border border-[#333] rounded-xl p-6 text-center">
                <p className="text-white/70 text-base">No accounts found for {selectedPersonName}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {personLedger.map((acc) => (
                  <div key={acc.id} className="bg-black border border-[#333] rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-base">{acc.account_name}</p>
                        {acc.phone && (
                          <p className="text-white/70 text-sm">{acc.phone}</p>
                        )}
                        {acc.account_no && (
                          <p className="text-white/70 text-sm">A/C: {acc.account_no}</p>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-black border border-[#333] rounded-lg p-3">
                        <p className="text-white/70">Principal</p>
                        <p className="font-semibold">₹{(acc.principal_amount || 0).toFixed(2)}</p>
                      </div>
                      <div className="bg-black border border-[#333] rounded-lg p-3">
                        <p className="text-white/70">EMI</p>
                        <p className="font-semibold">₹{(acc.emi_amount || 0).toFixed(2)}</p>
                      </div>
                      <div className="bg-black border border-[#333] rounded-lg p-3">
                        <p className="text-white/70">Collected</p>
                        <p className="font-semibold">₹{(acc.total_collected || 0).toFixed(2)}</p>
                      </div>
                      <div className="bg-black border border-[#333] rounded-lg p-3">
                        <p className="text-white/70">Outstanding</p>
                        <p className="font-semibold">₹{(acc.outstanding_due || 0).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Ledger Rows */}
        <div className="px-4 pb-10">
          <h2 className="text-xl font-bold text-white mb-3">{selectedPersonName === 'All' ? 'Central Ledger' : 'Ledger Rows'}</h2>
          {selectedPersonName === 'All' && centralAccounts.length > 0 && (
            <div className="overflow-x-auto mb-6">
              <table className="w-full">
                <thead>
                  <tr className="text-white/70 border-b border-[#333]">
                    <th className="text-left py-2 text-base">CIF</th>
                    <th className="text-left py-2 text-base">Account No</th>
                    <th className="text-left py-2 text-base">Dt. Lent</th>
                    <th className="text-right py-2 text-base">Fresh</th>
                    <th className="text-right py-2 text-base">No. of days</th>
                    <th className="text-right py-2 text-base">EMI</th>
                    <th className="text-right py-2 text-base">% Held</th>
                    <th className="text-right py-2 text-base">Amt. Held</th>
                    <th className="text-left py-2 text-base">As on Dt.</th>
                    <th className="text-left py-2 text-base">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {centralAccounts.map((acc) => {
                    const meta = centralAccountMeta[acc.id] || {};
                    const lent = (meta.loan_date ? new Date(meta.loan_date).toLocaleDateString('en-IN') : '-') as string;
                    const term = (meta.term_days ?? 0) as number;
                    const emi = (acc.emi_amount ?? 0) as number;
                    const fresh = (acc.principal_amount ?? 0) as number;
                    const heldPct = fresh ? `${( (meta.amount_held ?? 0) / fresh * 100 ).toFixed(2)}%` : '0.00%';
                    const heldAmt = (meta.amount_held ?? 0) as number;
                    return (
                      <tr key={acc.id} className="text-white border-b border-[#333]">
                        <td className="py-2 text-base">{acc.account_name}</td>
                        <td className="py-2 text-base">{acc.account_no || '-'}</td>
                        <td className="py-2 text-base">{lent}</td>
                        <td className="py-2 text-base text-right">{fresh.toLocaleString('en-IN')}</td>
                        <td className="py-2 text-base text-right">{term}</td>
                        <td className="py-2 text-base text-right">{emi.toLocaleString('en-IN')}</td>
                        <td className="py-2 text-base text-right">{heldPct}</td>
                        <td className="py-2 text-base text-right">{heldAmt.toLocaleString('en-IN')}</td>
                        <td className="py-2 text-base">{new Date().toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</td>
                        <td className="py-2 text-base">{(meta.status || '').toUpperCase() || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {personLedgerRows.length === 0 ? (
            <div className="bg-black border border-[#333] rounded-xl p-6 text-center">
              <p className="text-white/70 text-base">No ledger rows yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-white/70 border-b border-[#333]">
                    <th className="text-left py-2 text-base">Date</th>
                    <th className="text-left py-2 text-base">Account</th>
                    <th className="text-left py-2 text-base">Mode</th>
                    <th className="text-right py-2 text-base">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {personLedgerRows.map((r, idx) => (
                    <tr key={idx} className="text-white border-b border-[#333]">
                      <td className="py-2 text-base">{new Date(r.date).toLocaleDateString('en-IN')}</td>
                      <td className="py-2 text-base">{r.account_name}</td>
                      <td className="py-2 text-base">{r.mode}</td>
                      <td className="py-2 font-semibold text-base text-right">₹{Number(r.amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}

// Update Modal Component
interface UpdateModalProps {
  customer: Customer;
  onClose: () => void;
  onSubmit: (amount: string, mode: string) => void;
}

function UpdateModal({ customer, onClose, onSubmit }: UpdateModalProps) {
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('Cash');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(amount, mode);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="card-bg rounded-2xl p-6 w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-white">Update Collection</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-white text-lg font-medium mb-2">
              Customer: {customer.name}
            </label>
          </div>

          <div>
            <label className="block text-white text-lg font-medium mb-2">
              Amount
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <IndianRupee size={20} />
              </div>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg pl-12 pr-4 py-4 text-white min-text-22 focus:outline-none focus:border-green-500 transition-colors"
                placeholder="Enter amount"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-white text-lg font-medium mb-2">
              Payment Mode
            </label>
            <div className="flex flex-wrap gap-3">
              {['Cash', 'UPI', 'PP'].map((paymentMode) => (
                <button
                  key={paymentMode}
                  type="button"
                  onClick={() => setMode(paymentMode)}
                  className={`px-6 py-3 rounded-xl min-text-22 font-medium transition-colors ${
                    mode === paymentMode
                      ? paymentMode === 'Cash' ? 'bg-green-600 text-white' :
                        paymentMode === 'UPI' ? 'bg-purple-600 text-white' :
                        'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  {paymentMode}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-4 rounded-xl min-text-22 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!amount}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-800 disabled:text-gray-500 text-white py-4 rounded-xl min-text-22 font-medium transition-colors"
            >
              Add Collection
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default App;
